import { useAttachmentsUploadManager } from '@components'

import noop from 'lodash/noop'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useForm, UseFormReturn } from '../../../../../hooks'
import { SendAsEmailModalVariant } from '../../../enums/sendAsEmailModalVariant'
import { SendAsEmailForm } from '../../../types/sendAsEmailForm'
import { usePredefinedInvoiceEmailFormValues } from '../hooks/usePredefinedInvoiceEmailFormValues'
import { SendAsEmailFormSchema } from '../types/sendAsEmailFormSchema'
import { getSendAsEmailFormDefaultValues } from '../utils/getSendAsEmailFormDefaultValues'
import { getSendAsEmailFormSchema } from '../utils/getSendAsEmailFormSchema'

interface ContextState {
  isLoading: boolean
  isSubmitting: boolean
  sendAsEmailForm: UseFormReturn<SendAsEmailFormSchema>
  submitForm: () => void
  validate: () => Promise<boolean>
  variant: SendAsEmailModalVariant
}

const SendAsEmailFormContext = createContext<ContextState | undefined>(undefined)

interface SendAsEmailFormProviderProps {
  children?: ReactNode
  isSubmitting: boolean
  onSubmit: (formData: SendAsEmailForm) => void
  variant: SendAsEmailModalVariant
}

export const SendAsEmailFormContextProvider = ({
  children,
  isSubmitting,
  onSubmit,
  variant,
}: SendAsEmailFormProviderProps): ReactElement => {
  const { t } = useTranslation()
  const { predefinedFormValues, isLoading: isLoadingPredefinedValues } = usePredefinedInvoiceEmailFormValues(variant)
  const { hasFailedAttachments } = useAttachmentsUploadManager()

  const sendAsEmailForm = useForm<SendAsEmailFormSchema>({
    defaultValues: getSendAsEmailFormDefaultValues(),
    validationSchema: getSendAsEmailFormSchema(t, hasFailedAttachments),
  })

  const { Form, handleSubmit, reset: resetForm, trigger } = sendAsEmailForm

  // we're using a fake submit for validation due to how useForm's mode param works
  const handleValidation = useCallback(async () => {
    await handleSubmit(noop)()

    return trigger()
  }, [handleSubmit, trigger])

  const submitForm = useCallback(() => {
    handleSubmit(onSubmit)()
  }, [handleSubmit, onSubmit])

  useEffect(() => {
    if (predefinedFormValues && !isLoadingPredefinedValues) {
      resetForm(predefinedFormValues)
    }
  }, [isLoadingPredefinedValues, resetForm, predefinedFormValues])

  const contextValue = useMemo(
    () => ({
      isLoading: isLoadingPredefinedValues,
      isSubmitting,
      sendAsEmailForm,
      submitForm,
      validate: handleValidation,
      variant,
    }),
    [handleValidation, isLoadingPredefinedValues, isSubmitting, sendAsEmailForm, submitForm, variant],
  )

  return (
    <SendAsEmailFormContext.Provider value={contextValue}>
      <Form>{children}</Form>
    </SendAsEmailFormContext.Provider>
  )
}

export const useSendAsEmailForm = () => {
  const context = useContext(SendAsEmailFormContext)

  if (!context) {
    throw new Error('SendAsEmailFormContextProvider is missing in the module!')
  }

  return context
}
